/**
 * Recording that money arrived, from the money calendar itself.
 *
 * The calendar knows what was expected: who, how much, which contract or
 * deal, and when. Typing that back into a transaction form is the slowest
 * possible way to say "it came" — and while nobody types it, Reppr cannot
 * tell a missed paycheck from one nobody wrote down (shared/missedMoney).
 * So the projected check carries a control that records it.
 *
 * Nothing is recorded silently or in advance: the agent confirms the amount
 * and the date, both prefilled, and only a payment whose date has arrived
 * can be logged at all. A projected paycheck is priced at today's roster
 * status, so the amount is a starting point to correct, not a fact.
 *
 * A deal payment is recorded twice on purpose: the schedule entry is marked
 * paid, because that is the agency's own record of the deal, and the income
 * is written as a transaction, because that is the money. The calendar
 * already reconciles the two (a receipt matching a paid schedule entry is
 * not listed again), so the athlete sees one payment, not two.
 */

import { pb } from '../lib/pocketbase'
import { createTransaction } from './transactions'
import { assertNotHeld } from '../shared/nilGo'
import type { AgencyMoneyItem } from './moneyCalendar'

export interface SchedulePayment {
  date: string
  amount: number
  status?: string | null
  actual_date?: string | null
  description?: string | null
  [key: string]: unknown
}

/**
 * Marks the scheduled payment this receipt answers for, and returns the whole
 * schedule to write back. The entry is matched on its own date and amount,
 * never on position: schedules get reordered and edited, and paying off the
 * wrong instalment would leave a real one looking unpaid for ever.
 *
 * Returns null when no unpaid entry matches, so the caller can say so rather
 * than quietly marking something else.
 */
export function markSchedulePaid(
  schedule: SchedulePayment[],
  match: { date: string; amount: number; actual_date: string },
): SchedulePayment[] | null {
  const day = (s: unknown) => String(s ?? '').slice(0, 10)
  const index = schedule.findIndex(p =>
    p.status !== 'paid' && day(p.date) === day(match.date) && Number(p.amount) === Number(match.amount))
  if (index < 0) return null
  const next = schedule.slice()
  next[index] = { ...next[index], status: 'paid', actual_date: match.actual_date }
  return next
}

export interface LogArgs {
  item: AgencyMoneyItem
  /** What actually arrived, which the agent may have corrected. */
  amount: number
  /** The day it arrived, YYYY-MM-DD. */
  date: string
  userId: string
  agencyId: string
}

/** Records one expected payment as received. Throws with what went wrong. */
export async function logMoneyItem({ item, amount, date, userId, agencyId }: LogArgs): Promise<void> {
  if (!(amount > 0)) throw new Error('Enter the amount that arrived.')
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error('Enter the date it arrived as YYYY-MM-DD.')

  if (item.kind === 'deal_payment') {
    await assertNotHeld(pb as any, item.source_id, 'Recording this payment')
    const deal = await pb.collection('deals').getOne<Record<string, any>>(item.source_id)
    const schedule = Array.isArray(deal.payment_schedule) ? (deal.payment_schedule as SchedulePayment[]) : []
    const updated = markSchedulePaid(schedule, { date: item.date, amount: item.amount ?? amount, actual_date: date })
    if (!updated) {
      throw new Error(`The payment due ${item.date} is no longer on ${item.source_title ?? 'this deal'}'s schedule, or is already marked paid. Open the deal and check it.`)
    }
    await pb.collection('deals').update(item.source_id, { payment_schedule: updated })
  }

  await createTransaction({
    type: 'athlete_income',
    amount,
    transaction_date: date,
    description: describe(item),
    client_id: item.client_id,
    contract_id: item.kind === 'paycheck' ? item.source_id : null,
    deal_id: item.kind === 'deal_payment' ? item.source_id : null,
    agency_id: agencyId,
    status: 'completed',
  } as Parameters<typeof createTransaction>[0], userId)
}

function describe(item: AgencyMoneyItem): string {
  if (item.kind === 'paycheck') {
    const week = new Date(`${item.date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
    return `${item.source_title ?? 'Playing contract'} — check for the week of ${week}`
  }
  return [item.source_title ?? 'Deal payment', item.detail].filter(Boolean).join(' — ')
}

/**
 * Whether this row can be recorded as arrived. Money that has not been paid
 * yet cannot: a calendar that lets an agent tick off next month's cheque is
 * a calendar that lies to the athlete reading it.
 */
export function canLog(item: AgencyMoneyItem, today: string): boolean {
  if (item.kind !== 'paycheck' && item.kind !== 'deal_payment') return false
  if (item.status === 'received') return false
  if (item.amount == null || !(item.amount > 0)) return false
  return item.date <= today
}

export interface BatchResult {
  done: number
  /** Each payment that did not record, with what went wrong. */
  failed: { item: AgencyMoneyItem; message: string }[]
}

/**
 * Records several payments, one after another.
 *
 * Sequential on purpose: these are writes to the same agency's records, and a
 * burst of parallel PATCHes to one deal's schedule would race. One failure
 * does not stop the rest, and never disappears either — the caller gets every
 * payment that did not record and why, because a batch that silently drops a
 * payment would leave the calendar claiming money arrived when it did not.
 */
export async function logMoneyItems(
  entries: LogArgs[],
  log: (entry: LogArgs) => Promise<void> = logMoneyItem,
): Promise<BatchResult> {
  const failed: BatchResult['failed'] = []
  let done = 0
  for (const entry of entries) {
    try {
      await log(entry)
      done++
    } catch (err) {
      failed.push({ item: entry.item, message: (err as Error).message })
    }
  }
  return { done, failed }
}

/**
 * Says an assumed paycheck never arrived.
 *
 * Reppr records a past check as paid when the roster history had the athlete
 * on a paying roster that week (the daily missedMoney job, status "assumed").
 * This takes one back: the week is noted on the contract so it is never
 * assumed again, the assumed income is removed, and the missed-payment alert
 * then reports the week as missing. Deleting income needs the admin role
 * under the collection rules, so it goes through the server
 * (web/api/paycheck-not-received.ts), which checks the caller may record
 * income at all.
 */
export async function markNotReceived(transactionId: string): Promise<{ contract_id: string; date: string }> {
  const res = await fetch('/api/paycheck-not-received', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${pb.authStore.token}` },
    body: JSON.stringify({ transaction_id: transactionId }),
  })
  const text = await res.text()
  let data: Record<string, any>
  try {
    data = JSON.parse(text)
  } catch {
    throw new Error(`Marking the paycheck returned ${res.status} but not JSON. If you are running the dev server on its own, this endpoint only exists under \`vercel dev\`.`)
  }
  if (!res.ok) throw new Error(data.error || `Could not mark the paycheck (${res.status}).`)
  return data as { contract_id: string; date: string }
}
